import { v4 as uuidv4 } from 'uuid';
import { sha256 } from 'js-sha256';

export type ActiveNavItem = {
  href: string;
  length: number;
};

export type NavItemLike = {
  href?: string;
  items?: NavItemLike[];
};

/**
 * Function to debounce function calls
 *
 * Useful for limiting the rate at which a function can fire.
 *
 * Debounced resize event:
 *
 * const ref = useRef(null);
 * const handleResize = useCallback(
 *    debounce(ref => {
 *      console.log('RESIZED', ref);
 *    }, 200),
 *   []
 * );
 *
 * useEffect(() => {
 *     window.addEventListener('resize', handleResize);
 *     return () => {
 *         window.removeEventListener('resize', handleResize);
 *     };
 * }, []);
 *
 *
 * Debounced scroll event:
 *
 * const ref = useRef(null);
 * const handleScroll = useCallback(
 *    debounce(ref => {
 *      console.log('SCROLLED', ref);
 *    }, 200),
 *   []
 * );
 *
 * useEffect(() => {
 *     window.addEventListener('scroll', handleScroll);
 *     return () => {
 *         window.removeEventListener('scroll', handleScroll);
 *     };
 * }, []);
 *
 */
export function debounce(
  fn: (...args: any) => void,
  wait: number,
  immediate?: boolean,
) {
  let timeout: any;

  return (...args: any) => {
    const later = () => {
      timeout = null;
      if (!immediate) fn(...args);
    };

    const callNow: boolean = !!immediate && !timeout;

    clearTimeout(timeout);

    timeout = setTimeout(later, wait);

    if (callNow) fn(...args);
  };
}

/**
 * Function to check if an element is visible in the viewport
 */
export function isElementVisible(el: HTMLElement, heightDivisor: number = 2) {
  const rect = el.getBoundingClientRect(),
    vWidth = window.innerWidth || document.documentElement.clientWidth,
    vHeight =
      (window.innerHeight || document.documentElement.clientHeight) /
      heightDivisor;

  return (
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < vWidth &&
    rect.top < vHeight
  );
}

/**
 * Function to filter an object by allowed and disallowed keys
 */
export function filterObject(
  obj: Record<string, any>,
  allowed: Array<string> | null = null,
  disallowed: Array<string> | null = null,
  allowedRegex: RegExp | null = null,
  disallowedRegex: RegExp | null = null,
) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key]) =>
        (allowedRegex && allowedRegex.test(key)) ||
        ((!allowed || !allowed?.length || allowed.includes(key)) &&
          (!disallowed || !disallowed?.length || !disallowed.includes(key)) &&
          (!disallowedRegex || !disallowedRegex.test(key))),
    ),
  );
}

/**
 * Function to switch between dark and light mode
 */
export function toggleDarkMode() {
  const html = document.getElementsByTagName('html')[0];
  const isDarkMode = html.getAttribute('data-theme') === 'dark';
  html.setAttribute('data-theme', isDarkMode ? 'light' : 'dark');
}

/**
 * Function generate a random image in a specific dimension
 */
export function randomImage(width: number, height: number) {
  return `https://picsum.photos/${width}/${height}?` + uuidv4();
}

function randomizeImagesRecurse(
  item: { [key: string]: any },
  path: Array<string>,
  aspect: string | boolean,
) {
  const [key, ...remaining] = path,
    val = item[key];
  item = { ...item };
  if (!val) {
    return item;
  }
  if (remaining.length) {
    item[key] = randomizeImagesRecurse(val, remaining, aspect);
  } else {
    if (aspect) {
      item.aspect = ['landscape', 'portrait', 'square', 'wide'][
        Math.round(Math.random() * 3.0)
      ];
      switch (item.aspect) {
        case 'landscape':
          item.src = randomImage(900, 520);
          break;
        case 'portrait':
          item.src = randomImage(520, 900);
          break;
        case 'square':
          item.src = randomImage(900, 900);
          break;
        case 'wide':
          item.src = randomImage(1600, 500);
          break;
      }
    } else {
      item[key] = val.split('?')[0] + '?' + uuidv4();
    }
  }
  return item;
}

/**
 * Randomize images in a list of objects
 */
export function randomizeImages(
  items: Array<{ [key: string]: any }>,
  path: Array<string>,
  aspect: string | boolean = false,
) {
  return items.map((item) => randomizeImagesRecurse(item, path, aspect));
}

/**
 * Checks if two objects are equal ignoring order
 */
export function deepEqual(obj1: any, obj2: any) {
  if (obj1 === obj2) {
    return true;
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  if (
    obj1 == null ||
    typeof obj1 !== 'object' ||
    obj2 == null ||
    typeof obj2 !== 'object'
  ) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

/**
 * Hash data
 */
export function hashData(data: any) {
  const hash = sha256.create();
  hash.update(
    JSON.stringify(
      data,
      (key, value) => (key.match(/^[_@]/) ? null : value),
      // eslint-disable-next-line no-useless-escape
    ).replace(/http:\/\/[^\/]+(:\d+)*/, ''),
  );
  return hash.hex();
}

/**
 * Disable scrolling
 */
export function rememberScrollPosition() {
  const body = document?.body;
  body.setAttribute('data-scroll-position', String(window.scrollY));
  body.setAttribute(
    'data-header-visible',
    body.classList.contains('header-visible') ? '1' : '0',
  );
}

/**
 * Reset scroll position
 */
export function resetScrollPosition() {
  const body = document?.body;
  const pos =
    parseInt(body.getAttribute('data-scroll-position') ?? '0', 10) || 0;
  const headerVisible = body.getAttribute('data-header-visible');
  window.scrollTo({ top: pos });
  if (headerVisible) {
    const header = document.querySelector('.header');
    if (header) {
      header.classList.add('header--scrolling-up');
      header.classList.remove('header--scrolling-down');
      body.classList.add('header-visible');
    }
  }
}

/**
 * Normalize a path by removing trailing slashes
 */
const normalizePath = (path = ''): string => {
  const normalized = path.replace(/\/+$/, '');
  return normalized || '/';
};

/**
 * Check if a navigation item href matches the current pathname
 */
const isPathMatch = (href: string, pathname: string): boolean => {
  const itemPath = normalizePath(href);
  const currentPath = normalizePath(pathname);

  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
};

/**
 * Finds the best matching href for the current pathname across the entire navigation tree.
 *
 * It traverses all items (including nested ones) and returns the href
 * with the longest matching prefix.
 *
 * Why longest match?
 * Because deeper paths (e.g. /studium/bachelor) should win over higher-level ones (/studium).
 */
export const getActiveNavItemHref = (
  items: NavItemLike[] = [],
  pathname: string,
): string | null => {
  if (!Array.isArray(items) || items.length === 0 || !pathname) {
    return null;
  }

  const currentPath = normalizePath(pathname);

  let bestHref = '';
  let bestLength = -1;

  const stack: NavItemLike[] = [...items];

  while (stack.length > 0) {
    const item = stack.pop();
    if (!item) continue;

    const href =
      typeof item.href === 'string' && item.href.trim().length > 0
        ? item.href
        : '';

    if (href) {
      const itemPath = normalizePath(href);

      if (isPathMatch(itemPath, currentPath) && itemPath.length > bestLength) {
        bestHref = href;
        bestLength = itemPath.length;
      }
    }

    if (Array.isArray(item.items) && item.items.length > 0) {
      stack.push(...item.items);
    }
  }

  return bestHref || null;
};

/**
 * Determines if a navigation item should be marked as active.
 *
 * An item is active if:
 * - its own href matches the activeHref
 * - OR any of its descendants matches the activeHref
 *
 * This is required because top-level items often don't have their own href
 * and instead represent a group of nested links.
 */
export const isNavItemActive = (
  item: NavItemLike,
  activeHref?: string | null,
): boolean => {
  if (!activeHref) return false;

  const normalizedActive = normalizePath(activeHref);

  const check = (node: NavItemLike): boolean => {
    const href =
      typeof node.href === 'string' && node.href.trim().length > 0
        ? normalizePath(node.href)
        : '';

    if (href === normalizedActive) {
      return true;
    }

    if (node.items?.length) {
      return node.items.some(check);
    }

    return false;
  };

  return check(item);
};

/**
 * Finds the best matching href among top-level navigation items.
 *
 * Since top-level items may not have their own href,
 * this function falls back to the first child item to determine their effective path.
 *
 * This is mainly used for highlighting top-level navigation entries.
 */
export const getActiveTopLevelHref = (
  items: NavItemLike[] = [],
  pathname: string,
): string | null => {
  if (!pathname || !items.length) return null;

  const currentPath = normalizePath(pathname);

  let bestMatch: { href: string; length: number } | null = null;

  const getItemHref = (item: NavItemLike): string => {
    if (item.href) return normalizePath(item.href);

    // 🔥 fallback auf erstes child
    if (item.items?.length) {
      return getItemHref(item.items[0]);
    }

    return '';
  };

  for (const item of items) {
    const href = getItemHref(item);

    if (href && isPathMatch(href, currentPath)) {
      if (!bestMatch || href.length > bestMatch.length) {
        bestMatch = { href, length: href.length };
      }
    }
  }

  return bestMatch?.href || null;
};
